import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { flushSync } from 'react-dom';
import OktaSignIn from '@okta/okta-signin-widget';
import {
  shouldShowCustomUI,
  isEmailMfaState,
  isSmsMfaState,
  isTotpState,
  OKTA_INTERNAL_LOADING_STATES,
  WIDGET_STATES,
} from '../config/widgetStates';
import {
  getTranslatedErrorMessage,
  normalizeOktaError,
  getWidgetInitError,
  getWidgetNotLoadedError,
  getAuthNotReadyError,
  getAuthNotReadyWaitError,
  getGoogleNotConfiguredError,
  getGoogleSignInFailedError,
  getMfaCodeSubmitError,
  getMfaCodeResendError,
} from '../utils/errorMessages';
import {
  resolveControllerName,
  findBackLink,
  findAuthContent,
  getGoogleIdpId,
  getPreviousViewState,
  canNavigateBack,
  applyMfaCustomizations,
  submitCredentialsToForm,
  getCredentialsFromForm,
  submitPasswordResetForm,
  submitPasswordExpiredForm,
  clickPasswordExpiredFooterLink,
  getPasswordExpiryInfo,
  getAvailableMfaFactors,
  clickMfaEnrollButton,
  MFA_FACTOR_KEYS,
  getTotpEnrollmentData,
  clickTotpNextButton,
  clickCantScanLink,
  submitTotpCode as submitTotpCodeToWidget,
  findMfaCodeInput,
  submitMfaCode as submitMfaCodeToWidget,
  clickSendEmailCode,
  clickResendEmailCode,
  setSmsCountryCode,
  setSmsPhoneNumber as setSmsPhoneInput,
  clickSmsSendCode,
  clickSmsResendCode,
  isSmsCodeInputVisible,
  submitSmsVerificationCode as submitSmsCodeToWidget,
  getSmsVerifyMaskedPhone,
  getOktaFormErrorText,
  resolveViewState,
} from '../utils/oktaWidgetHelpers';
import { utils } from 'utils';
import { getWidgetConfig } from '../utils';
import { reportErrorToDataDog } from '../../../utils/datadog';

import '@okta/okta-signin-widget/css/okta-sign-in.min.css';

const BACK_NAVIGATION_DESTINATIONS = new Set([
  WIDGET_STATES.ENROLL_CHOICES,
  WIDGET_STATES.PRIMARY_AUTH,
]);

const MFA_SUBMIT_PAGEVIEW_STATES = new Set([
  WIDGET_STATES.MFA_ENROLL_ACTIVATE_EMAIL,
  WIDGET_STATES.MFA_VERIFY_EMAIL,
  WIDGET_STATES.MFA_VERIFY_SMS,
  WIDGET_STATES.ACTIVATE_TOTP,
  WIDGET_STATES.MFA_VERIFY_GOOGLE_AUTH,
]);

export const useOktaWidget = () => {
  const config = useMemo(() => getWidgetConfig(), []);
  const trackEvent = utils.eventsLogger?.unifiedLoginDio?.track;
  const [containerElement, setContainerElement] = useState(null);
  const [isWidgetRendered, setIsWidgetRendered] = useState(false);
  const [oktaViewState, setOktaViewState] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mfaConfig, setMfaConfig] = useState(null);
  const [totpData, setTotpData] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [loginPrefill, setLoginPrefill] = useState(null);
  const [passwordExpiryInfo, setPasswordExpiryInfo] = useState(null);
  const [isTokenExpired, setIsTokenExpired] = useState(config.recoveryToken ? null : false);

  const [smsCodeSent, setSmsCodeSent] = useState(false);
  const [smsPhoneNumber, setSmsPhoneNumber] = useState('');

  const widgetRef = useRef(null);
  const oktaViewStateRef = useRef('');
  const viewStateHistoryRef = useRef([WIDGET_STATES.PRIMARY_AUTH]);
  const backLinkRef = useRef(null);
  const isNavigatingBackRef = useRef(false);
  const smsEnrollResultObserverRef = useRef(null);

  const containerRef = useCallback(node => {
    if (node) setContainerElement(node);
  }, []);

  const showCustomUI = shouldShowCustomUI(oktaViewState);

  const clearError = useCallback(() => setError(''), []);

  const handleBackNavigation = useCallback(() => {
    if (isNavigatingBackRef.current) {
      const targetState = [...viewStateHistoryRef.current]
        .reverse()
        .find(s => BACK_NAVIGATION_DESTINATIONS.has(s));

      if (targetState) {
        flushSync(() => {
          setOktaViewState(targetState);
          setError('');
        });
      }
      return;
    }

    const history = viewStateHistoryRef.current;

    if (!canNavigateBack(history)) return;

    const previousState = getPreviousViewState(history);
    if (!shouldShowCustomUI(previousState)) return;

    flushSync(() => {
      setOktaViewState(previousState);
      setError('');
      viewStateHistoryRef.current = history.slice(0, -1);
    });
  }, []);

  const updateViewStateHistory = useCallback(viewState => {
    if (OKTA_INTERNAL_LOADING_STATES.includes(viewState)) return;

    const history = viewStateHistoryRef.current;
    if (history[history.length - 1] !== viewState) {
      viewStateHistoryRef.current = [...history, viewState];
    }
  }, []);

  const handleEmailMfaRender = useCallback(container => {
    setError('');

    const isInitialEnrollScreen = !findMfaCodeInput(container);
    if (isInitialEnrollScreen) {
      const sent = clickSendEmailCode(container);
      if (!sent) setError(getMfaCodeResendError());
    }
  }, []);

  const cleanupSmsEnrollResultObserver = useCallback(() => {
    if (smsEnrollResultObserverRef.current) {
      smsEnrollResultObserverRef.current.disconnect();
      smsEnrollResultObserverRef.current = null;
    }
  }, []);

  const handleSmsMfaRender = useCallback((container, viewState) => {
    setError('');

    if (viewState === WIDGET_STATES.MFA_VERIFY_SMS) {
      setSmsPhoneNumber(getSmsVerifyMaskedPhone(container));
      clickSmsSendCode(container);
      setSmsCodeSent(true);
      return;
    }

    setSmsCodeSent(isSmsCodeInputVisible(container));

    const smsEnrollResultObserver = new MutationObserver(() => {
      if (isSmsCodeInputVisible(container)) {
        setSmsCodeSent(true);
        setIsSubmitting(false);
        return;
      }

      const oktaRejected = container.querySelector('.o-form-has-errors');
      if (oktaRejected) {
        setError(getOktaFormErrorText(container) || getMfaCodeSubmitError());
        setIsSubmitting(false);
      }
    });
    smsEnrollResultObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
    smsEnrollResultObserverRef.current = smsEnrollResultObserver;
  }, []);

  const handleTotpRender = useCallback(
    (container, viewState, previousViewState) => {
      if (viewState !== previousViewState) {
        setError('');
      }
      const { qrCodeSrc, manualSetupKey } = getTotpEnrollmentData(container);
      setTotpData(prev => ({
        ...prev,
        ...(qrCodeSrc && { qrCodeSrc }),
        ...(manualSetupKey && { manualSetupKey }),
      }));
    },
    []
  );

  useEffect(() => {
    if (!config || !containerElement || widgetRef.current) return;

    const widget = new OktaSignIn(config);
    widgetRef.current = widget;

    widget.on('afterRender', ctx => {
      const rawController = resolveControllerName(ctx, containerElement);
      const viewState = resolveViewState(rawController, containerElement);
      const isLoadingState = OKTA_INTERNAL_LOADING_STATES.includes(viewState);

      if (isNavigatingBackRef.current) {
        if (!BACK_NAVIGATION_DESTINATIONS.has(viewState)) {
          const backLink = findBackLink(containerElement);
          if (backLink) {
            backLink.click();
            return;
          }
        }
        isNavigatingBackRef.current = false;
      }

      const previousViewState = oktaViewStateRef.current;
      oktaViewStateRef.current = viewState;
      setOktaViewState(viewState);
      setIsSubmitting(false);
      if (!isLoadingState) {
        setIsWidgetRendered(true);
        setIsTokenExpired(false);
      }

      updateViewStateHistory(viewState);

      if (trackEvent && previousViewState !== viewState) {
        if (viewState === WIDGET_STATES.ENROLL_CHOICES) {
          trackEvent('registration_MFA', 'pageview');
        }

        if (MFA_SUBMIT_PAGEVIEW_STATES.has(viewState)) {
          trackEvent('MFA_Submit', 'pageview', { state: viewState });
        }
      }

      if (viewState === WIDGET_STATES.ENROLL_CHOICES) {
        const availableMfaFactors = getAvailableMfaFactors(containerElement);
        if (availableMfaFactors.size > 0) {
          const newMfaConfig = MFA_FACTOR_KEYS.reduce((cfg, key) => {
            cfg[key] = {
              available: availableMfaFactors.has(key),
              onSelect: () => clickMfaEnrollButton(containerElement, key),
            };
            return cfg;
          }, {});
          setMfaConfig(newMfaConfig);
        }
      }

      if (isTotpState(viewState)) {
        handleTotpRender(containerElement, viewState, previousViewState);
      }

      if (isEmailMfaState(viewState)) {
        handleEmailMfaRender(containerElement);
      }

      cleanupSmsEnrollResultObserver();

      if (isSmsMfaState(viewState)) {
        handleSmsMfaRender(containerElement, viewState);
      }

      const authContent = findAuthContent(containerElement);
      if (authContent) {
        authContent.style.display = isLoadingState ? 'none' : '';
      }

      applyMfaCustomizations();

      if (backLinkRef.current) {
        backLinkRef.current.removeEventListener('click', handleBackNavigation);
      }
      const backLink = findBackLink(containerElement);
      if (backLink) {
        backLink.addEventListener('click', handleBackNavigation);
        backLinkRef.current = backLink;
      }

      if (viewState === WIDGET_STATES.PRIMARY_AUTH) {
        const prefill = getCredentialsFromForm(containerElement);
        setLoginPrefill(prev => {
          if (
            prev?.email === prefill.email &&
            prev?.rememberMe === prefill.rememberMe
          ) {
            return prev;
          }
          return prefill;
        });
      }

      if (viewState === WIDGET_STATES.PASSWORD_EXPIRED) {
        setPasswordExpiryInfo(getPasswordExpiryInfo(containerElement));
      }
    });

    widget.on('afterError', (ctx, err) => {
      isNavigatingBackRef.current = false;

      const errorCode = err?.xhr?.responseJSON?.errorCode || err?.errorCode;
      if (config.recoveryToken && errorCode === 'E0000105') {
        setIsTokenExpired(true);
        return;
      }
      setError(getTranslatedErrorMessage(normalizeOktaError(err)));
      setIsSubmitting(false);
    });

    widget.showSignInAndRedirect({ el: containerElement }).catch(err => {
      reportErrorToDataDog('Okta widget initialization failed', {
        error: err,
        errorLocation: 'useOktaWidget.showSignInAndRedirect',
      });
      setError(getTranslatedErrorMessage(normalizeOktaError(err)) ||
          getWidgetInitError());
      setIsSubmitting(false);
      setIsWidgetRendered(false);
    });

    return () => {
      cleanupSmsEnrollResultObserver();
      if (backLinkRef.current) {
        backLinkRef.current.removeEventListener('click', handleBackNavigation);
        backLinkRef.current = null;
      }
      if (widgetRef.current) {
        widgetRef.current.remove();
        widgetRef.current = null;
      }
    };
  }, [
    config,
    containerElement,
    trackEvent,
    handleBackNavigation,
    updateViewStateHistory,
    handleEmailMfaRender,
    handleTotpRender,
    cleanupSmsEnrollResultObserver,
    handleSmsMfaRender,
  ]);

  const submitCredentials = useCallback(
    (email, password, rememberMe) => {
      setIsSubmitting(true);
      setError('');

      if (!containerElement) {
        setError(getWidgetNotLoadedError());
        setIsSubmitting(false);
        return false;
      }

      const result = submitCredentialsToForm(
        containerElement,
        email,
        password,
        rememberMe
      );

      if (!result.success) {
        setError(getAuthNotReadyError());
        setIsSubmitting(false);
        return false;
      }

      setUserEmail(email);
      return true;
    },
    [containerElement]
  );

  const changeExpiredPassword = useCallback(
    ({ oldPassword, newPassword, confirmPassword }) => {
      setIsSubmitting(true);
      setError('');

      if (!containerElement) {
        setError(getWidgetNotLoadedError());
        setIsSubmitting(false);
        return false;
      }

      const result = submitPasswordExpiredForm(containerElement, {
        oldPassword,
        newPassword,
        confirmPassword,
      });

      if (!result.success) {
        setError(getAuthNotReadyError());
        setIsSubmitting(false);
        return false;
      }

      return true;
    },
    [containerElement]
  );

  const signOutFromPasswordExpired = useCallback(() => {
    setError('');

    if (!containerElement) {
      setError(getWidgetNotLoadedError());
      return false;
    }

    const signedOut = clickPasswordExpiredFooterLink(
      containerElement,
      '.js-signout'
    );
    if (!signedOut) {
      setError(getAuthNotReadyError());
      return false;
    }

    return true;
  }, [containerElement]);

  const remindLaterPasswordExpired = useCallback(() => {
    setError('');

    if (!containerElement) {
      setError(getWidgetNotLoadedError());
      return false;
    }

    const skipped = clickPasswordExpiredFooterLink(
      containerElement,
      '.js-skip'
    );
    if (!skipped) {
      setError(getAuthNotReadyError());
      return false;
    }

    return true;
  }, [containerElement]);

  const createPassword = useCallback(
    password => {
      setIsSubmitting(true);
      setError('');

      if (!containerElement) {
        setError(getWidgetNotLoadedError());
        setIsSubmitting(false);
        return false;
      }

      let result;
      try {
        result = submitPasswordResetForm(containerElement, password);
      } catch {
        setError(getAuthNotReadyError());
        setIsSubmitting(false);
        return false;
      }

      if (!result.success) {
        setError(getAuthNotReadyError());
        setIsSubmitting(false);
        return false;
      }

      return true;
    },
    [containerElement]
  );

  const triggerGoogleSignIn = useCallback(() => {
    if (!widgetRef.current) {
      setError(getAuthNotReadyWaitError());
      return false;
    }

    const googleIdp = getGoogleIdpId(config);
    if (!googleIdp) {
      setError(getGoogleNotConfiguredError());
      return false;
    }

    try {
      widgetRef.current.authClient.token.getWithRedirect({
        scopes: ['openid', 'profile', 'email'],
        idp: googleIdp,
      });
      return true;
    } catch (err) {
      setError(getGoogleSignInFailedError());
      return false;
    }
  }, [config]);

  const advanceTotpEnrollment = useCallback(() => {
    if (!containerElement) return false;

    return clickTotpNextButton(containerElement);
  }, [containerElement]);

  const revealManualSetupKey = useCallback(() => {
    if (!containerElement) return false;

    return clickCantScanLink(containerElement);
  }, [containerElement]);

  const createSubmitHandler = useCallback(
    (widgetSubmitFn, getErrorMsg) => (code, rememberDevice = false) => {
      if (trackEvent) {
        trackEvent('MFA_Submit', 'click', { action: 'verify_code_submit' });
      }

      setIsSubmitting(true);
      setError('');

      if (!containerElement) {
        setError(getErrorMsg());
        setIsSubmitting(false);
        return false;
      }

      const result = widgetSubmitFn(containerElement, code, rememberDevice);
      if (!result.success) {
        setError(getErrorMsg());
        setIsSubmitting(false);
        return false;
      }

      return true;
    },
    [containerElement, trackEvent]
  );

  const submitTotpCode = useMemo(
    () => createSubmitHandler(submitTotpCodeToWidget, getAuthNotReadyError),
    [createSubmitHandler]
  );

  const submitMfaVerificationCode = useMemo(
    () => createSubmitHandler(submitMfaCodeToWidget, getMfaCodeSubmitError),
    [createSubmitHandler]
  );

  const submitSmsVerificationCode = useMemo(
    () => createSubmitHandler(submitSmsCodeToWidget, getMfaCodeSubmitError),
    [createSubmitHandler]
  );

  const resendMfaCode = useCallback(() => {
    if (trackEvent) {
      trackEvent('MFA_Submit', 'click', { action: 'resend_code' });
    }

    if (!containerElement) {
      setError(getMfaCodeResendError());
      return false;
    }

    setError('');
    const sent = clickResendEmailCode(containerElement);
    if (!sent) {
      setError(getMfaCodeResendError());
      return false;
    }

    return true;
  }, [containerElement, trackEvent]);

  const sendSmsCode = useCallback(
    (countryCode, phoneNumber, dialCode) => {
      setIsSubmitting(true);
      setError('');

      if (!containerElement) {
        setError(getMfaCodeSubmitError());
        setIsSubmitting(false);
        return false;
      }

      setSmsCountryCode(containerElement, countryCode);
      setSmsPhoneInput(containerElement, phoneNumber);

      const sent = clickSmsSendCode(containerElement);
      if (!sent) {
        setError(getMfaCodeSubmitError());
        setIsSubmitting(false);
        return false;
      }

      const displayNumber = dialCode
        ? `${dialCode} ${phoneNumber}`
        : phoneNumber;
      setSmsPhoneNumber(displayNumber);
      return true;
    },
    [containerElement]
  );

  const resendSmsCode = useCallback(() => {
    if (trackEvent) {
      trackEvent('MFA_Submit', 'click', { action: 'resend_code' });
    }

    if (!containerElement) {
      setError(getMfaCodeResendError());
      return false;
    }

    setError('');
    const sent = clickSmsResendCode(containerElement);
    if (!sent) {
      setError(getMfaCodeResendError());
      return false;
    }

    return true;
  }, [containerElement, trackEvent]);

  const navigateBackToEnrollChoices = useCallback(() => {
    if (!containerElement) return false;

    const backLink = findBackLink(containerElement);
    if (!backLink) return false;

    setTotpData(null);
    isNavigatingBackRef.current = true;
    setSmsCodeSent(false);
    setSmsPhoneNumber('');
    backLink.click();
    return true;
  }, [containerElement]);

  const hasGoogleIDP = Boolean(config?.idps?.some(idp => idp.type === 'GOOGLE'));

  return {
    containerRef,
    isWidgetRendered,
    error,
    isSubmitting,
    showCustomUI,
    oktaViewState,
    mfaConfig,
    totpData,
    userEmail,
    loginPrefill,
    passwordExpiryInfo,
    submitCredentials,
    changeExpiredPassword,
    signOutFromPasswordExpired,
    remindLaterPasswordExpired,
    createPassword,
    triggerGoogleSignIn,
    advanceTotpEnrollment,
    revealManualSetupKey,
    submitTotpCode,
    navigateBackToEnrollChoices,
    submitMfaVerificationCode,
    resendMfaCode,

    clearError,
    hasGoogleIDP,
    isTokenExpired,
    smsCodeSent,
    smsPhoneNumber,
    sendSmsCode,
    resendSmsCode,
    submitSmsVerificationCode,
  };
};

export default useOktaWidget;
